// Feed Attribute Coverage — Feed Health section, deterministic check (Merchant API).
// Completeness of essential vs recommended feed attributes across the catalog.

import { count, pct } from '../../utils/format';
import type { EvidenceProps } from './registry';

interface AttrCoverage {
  attribute: string;
  tier: 'essential' | 'recommended';
  presentCount: number;
  coveragePct: number;
}

interface FeedAttrShape {
  merchantId: string;
  productsReviewed: number;
  truncated: boolean;
  essential: AttrCoverage[];
  recommended: AttrCoverage[];
}

const CoverageTable = ({ rows, total }: { rows: AttrCoverage[]; total: number }) => (
  <table className='evidence__table evidence__table--feed-attrs'>
    <thead>
      <tr>
        <th>Attribute</th>
        <th className='evidence__th--right'>Coverage</th>
        <th className='evidence__th--right'>Products</th>
      </tr>
    </thead>
    <tbody>
      {rows.map(a => {
        const low = a.coveragePct < 0.9;
        return (
          <tr key={a.attribute} className={low ? 'evidence__row--flagged' : undefined}>
            <td>{a.attribute}</td>
            <td className='evidence__td--right'>{pct(a.coveragePct)}</td>
            <td className='evidence__td--right'>
              {count(a.presentCount)}/{count(total)}
            </td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

export const FeedAttributeCoverageEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as FeedAttrShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Essential attributes
          <span className='evidence__section-tag evidence__section-tag--context'>
            {count(e.productsReviewed)}
            {e.truncated ? '+' : ''} products · MC {e.merchantId}
          </span>
        </h4>
        <CoverageTable rows={e.essential} total={e.productsReviewed} />
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>Recommended attributes</h4>
        <CoverageTable rows={e.recommended} total={e.productsReviewed} />
      </section>

      <p className='evidence__footnote'>
        Coverage is measured directly from the feed data (not Google's disapproval flags — those are
        the Product Feed Health row). Essential-attribute gaps limit eligibility and matching;
        recommended attributes (GTINs, product types, additional images, custom labels) improve
        matching and segmentation, though applicability varies by product type.
      </p>
    </div>
  );
};
